/**
 * BANK vs PUSH.
 *
 * The bet has to be legible without arithmetic, so both numbers are on the
 * buttons: "BANK 400" against "PUSH for 1,000". The decision window is shorter
 * than the answer window and it auto-banks on timeout (spec 2 — punish not
 * knowing, don't punish hesitating), which is why the ring here is a separate,
 * differently-toned countdown rather than a continuation of the rung ring.
 *
 * WHAT THE STAKE LINE IS FOR. Playtesters read "Push · rung 2" as an offer to
 * have another go at the globe, and only worked out that it buys a QUESTION
 * two or three matches in. It is not hard to see why: they had just tapped a
 * globe, the button offered more points for doing something, and the only word
 * distinguishing the two was one the game never defined anywhere.
 *
 * So the sentence above the buttons says what is being bet and what it buys,
 * in nouns, naming the place they just found. It names the KIND of thing they
 * are about to be asked and never the thing itself: the target is already
 * public by now (they pinned it), so a category plus a target would be the
 * whole question handed over before the clock starts, which is thinking time
 * invariant 5 does not mean to give away.
 */

import { points, rungTitle } from '../lib/format';
import { CountdownRing } from './CountdownRing';
import { Flame } from './Flame';

export interface DecisionBarProps {
  readonly potNow: number;
  readonly potIfPushed: number;
  readonly nextRung: number;
  readonly canPush: boolean;
  /**
   * What the player just found — the country, the city, the state. Names the
   * bet: "a question about Peru" is a sentence somebody can act on, "a
   * question" is not.
   *
   * Null while the target is still sealed, which cannot happen on this screen
   * (the decision follows a resolved pinpoint) but is typed for honestly.
   */
  readonly targetLabel: string | null;
  /**
   * CHANGE 6. This push enters the golden round's terminal rung, where the
   * multiplier is tripled. The number on the button already reflects it — this
   * only makes the button look like what the number says.
   */
  readonly goldenPush?: boolean;
  readonly deadlineServerTs: number | null;
  readonly decisionMs: number;
  readonly serverNow: () => number;
  readonly onBank: () => void;
  readonly onPush: () => void;
}

export function DecisionBar({
  potNow,
  potIfPushed,
  nextRung,
  canPush,
  targetLabel,
  goldenPush = false,
  deadlineServerTs,
  decisionMs,
  serverNow,
  onBank,
  onPush,
}: DecisionBarProps): React.JSX.Element {
  const about = targetLabel === null ? 'this place' : targetLabel;

  return (
    <div className="gg-decision-group">
      {canPush ? (
        <p className="gg-stake" data-golden={goldenPush ? 'true' : 'false'}>
          Keep <strong>{points(potNow)}</strong>, or bet all of it on{' '}
          <strong>
            {goldenPush ? 'one open answer' : 'one trivia question'} about {about}
          </strong>
          .
        </p>
      ) : (
        <p className="gg-stake">
          You cleared the last one. There is nothing left to bet on, so bank it.
        </p>
      )}

      <div className="gg-decision flex w-full items-stretch gap-2">
        <button type="button" className="gg-btn gg-btn-bank flex-1" onClick={onBank}>
          <span className="gg-btn-kicker">Bank</span>
          <span className="gg-btn-value tabular-nums">{points(potNow)}</span>
        </button>

        <div className="flex shrink-0 flex-col items-center justify-center px-1">
          <CountdownRing
            deadlineServerTs={deadlineServerTs}
            durationMs={decisionMs}
            serverNow={serverNow}
            tone="decision"
            size={56}
          />
        </div>

        <button
          type="button"
          className="gg-btn gg-btn-push flex-1"
          data-golden={goldenPush ? 'true' : 'false'}
          onClick={onPush}
          disabled={!canPush}
        >
          <span className="gg-btn-kicker">
            {goldenPush ? (
              <>
                <Flame size={12} /> Golden rung
              </>
            ) : (
              <>Push · {rungTitle(nextRung)}</>
            )}
          </span>
          <span className="gg-btn-value tabular-nums">for {points(potIfPushed)}</span>
        </button>
      </div>
    </div>
  );
}
